import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import { CheckCircle2, ArrowRight, Sparkles, FileText, Download } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function ResumeBuilder() {
  return (
    <>
      <Helmet>
        <title>AI Resume Builder - Create ATS-Optimized Resumes | LoopBaan.ai</title>
        <meta name="description" content="Build professional, ATS-optimized resumes in minutes with AI. Get past applicant tracking systems and land more interviews." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <MegaMenuNav />

        {/* Hero Section */}
        <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">AI-POWERED</Badge>
              <h1 className="mb-6 text-5xl md:text-7xl font-bold leading-tight">
                Resume Builder That Gets You{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Hired
                </span>
              </h1>
              <p className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto">
                AI creates professional, ATS-optimized resumes tailored to your experience. Beat the bots, impress recruiters, land interviews.
              </p>
              <div className="flex justify-center">
                <Link to="/signup">
                  <Button size="lg" className="gap-2">
                    Build Your Resume <ArrowRight className="h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
              {[
                {
                  icon: Sparkles,
                  title: "AI-Powered Writing",
                  description: "Our AI analyzes your experience and writes compelling bullet points that showcase your achievements."
                },
                {
                  icon: FileText,
                  title: "ATS-Optimized Format",
                  description: "Pass applicant tracking systems with proper formatting, keywords, and structure that robots love."
                },
                {
                  icon: Download,
                  title: "Multiple Formats",
                  description: "Export to PDF, DOCX, or plain text. Choose from professional templates designed for your industry."
                }
              ].map((feature, i) => (
                <Card key={i} className="p-6 hover:shadow-lg transition-all">
                  <feature.icon className="h-12 w-12 text-primary mb-4" />
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">How It Works</h2>
              <div className="space-y-8">
                {[
                  {
                    step: "1",
                    title: "Enter Your Information",
                    description: "Add your work experience, education, skills, and achievements. Our AI guides you through each section."
                  },
                  {
                    step: "2",
                    title: "AI Enhances Your Content",
                    description: "Watch as AI transforms basic bullet points into powerful, achievement-focused statements that impress recruiters."
                  },
                  {
                    step: "3",
                    title: "Export & Apply",
                    description: "Download your professional resume in PDF or DOCX format. Make unlimited updates and versions."
                  }
                ].map((item, i) => (
                  <div key={i} className="flex gap-6">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-xl">
                        {item.step}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                      <p className="text-muted-foreground text-lg">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-center mb-12">Why Choose Our Resume Builder?</h2>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  "Beat applicant tracking systems (ATS)",
                  "Save hours of formatting and writing",
                  "Professional templates for all industries",
                  "Unlimited updates and versions",
                  "Keyword optimization for each job",
                  "Export to PDF, DOCX, or plain text",
                  "Mobile-friendly editing",
                  "Version history and backups"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Build Your Perfect Resume?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of job seekers who landed their dream jobs with our AI resume builder.
            </p>
            <Link to="/signup">
              <Button size="lg" className="gap-2">
                Get Started Free <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
